/**
 * Cross-tab privacy fence for every browser store that can hold tenant data.
 *
 * BroadcastChannel/storage events are deliberately not the correctness
 * boundary: both are delivered as later tasks. The latest tiny reset journal
 * also lives in localStorage, which is synchronously visible to same-origin
 * tabs. Every persistent read/write checks that journal around its asynchronous
 * IndexedDB work, so an already-running response cannot repopulate a store
 * after another tab's sign-out clear has completed.
 */

const RESET_STORAGE_KEY = 'blasto-protected-offline-reset-v1';
const RESET_CHANNEL = 'blasto-protected-offline-reset-v1';
const INITIAL_RESET_EPOCH = 'before-first-reset';
const RESET_RETRY_MS = 1_000;

interface OfflinePrivacyReset {
  id: string;
  source: string;
  /**
   * Cross-tab order, expressed in wall-clock microseconds and advanced beyond
   * every journal value this tab has observed. The id breaks the vanishingly
   * small tie between two tabs beginning in the same microsecond.
   */
  epoch: number;
  phase: 'pending' | 'completed';
}

export interface OfflinePrivacyResetDelivery {
  id: string;
  /** True when a previous physical-clear attempt for this same reset failed. */
  isRetry: boolean;
}

type ResetListenerResult = boolean | void;
type ResetListener = (delivery: OfflinePrivacyResetDelivery) => ResetListenerResult | Promise<ResetListenerResult>;

const source = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
let sequence = 0;
let channel: BroadcastChannel | null = null;
const listeners = new Set<ResetListener>();
const localResetWork = new Map<string, Promise<void>>();
const attemptedResetIds = new Set<string>();
const MAX_ATTEMPTED_RESET_IDS = 32;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function parseReset(value: unknown): OfflinePrivacyReset | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<OfflinePrivacyReset>;
  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.source !== 'string' ||
    (candidate.phase !== 'pending' && candidate.phase !== 'completed')
  ) {
    return null;
  }
  // Markers from an earlier preview build had no epoch. They remain readable
  // as the oldest possible journal entry and will be replaced on the next wipe.
  const epoch =
    typeof candidate.epoch === 'number' && Number.isSafeInteger(candidate.epoch) && candidate.epoch >= 0
      ? candidate.epoch
      : 0;
  return { id: candidate.id, source: candidate.source, epoch, phase: candidate.phase };
}

function persistedReset(): OfflinePrivacyReset | null {
  try {
    // Inside the try: a browser that blocks storage throws from the getter.
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(RESET_STORAGE_KEY);
    return raw ? parseReset(JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function persistReset(message: OfflinePrivacyReset): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(RESET_STORAGE_KEY, JSON.stringify(message));
  } catch {
    // BroadcastChannel remains a best-effort fallback in restricted modes.
  }
  try {
    channel?.postMessage(message);
  } catch {
    // The persisted journal remains authoritative.
  }
}

function compareReset(left: OfflinePrivacyReset, right: OfflinePrivacyReset): number {
  if (left.epoch !== right.epoch) return left.epoch < right.epoch ? -1 : 1;
  if (left.id !== right.id) return left.id < right.id ? -1 : 1;
  if (left.phase === right.phase) return 0;
  return left.phase === 'completed' ? 1 : -1;
}

function adoptReset(candidate: OfflinePrivacyReset | null): void {
  if (!candidate || (latestReset && compareReset(candidate, latestReset) < 0)) return;
  latestReset = candidate;
}

function refreshLatestReset(): void {
  adoptReset(persistedReset());
}

// A completed marker already present when this JavaScript process starts is a
// baseline: its wipe finished before this load. A pending marker is different.
// The issuing tab may have crashed after publishing it, so the first mounted
// subscriber must repeat the idempotent clears before anything can use storage.
let latestReset = persistedReset();
let observedResetId = latestReset?.phase === 'completed' ? latestReset.id : null;

function clockEpoch(): number {
  if (typeof performance !== 'undefined') {
    const highResolutionNow = performance.timeOrigin + performance.now();
    if (Number.isFinite(highResolutionNow)) return Math.floor(highResolutionNow * 1_000);
  }
  return Date.now() * 1_000;
}

function scheduleResetRetry(): void {
  if (retryTimer !== null) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    refreshLatestReset();
    deliverLatestReset();
  }, RESET_RETRY_MS);
}

/**
 * Complete a reset only after this tab's idempotent clears have confirmed
 * success. Any tab may repair an issuer that disappeared mid-wipe.
 */
export function finishOfflinePrivacyReset(id: string): void {
  refreshLatestReset();
  if (latestReset?.id !== id) return;
  if (latestReset.phase === 'pending') {
    latestReset = { ...latestReset, source, phase: 'completed' };
    persistReset(latestReset);
  }
  observedResetId = id;
}

/** Ask the mounted session owner to repeat a failed pending wipe. */
export function retryOfflinePrivacyReset(): void {
  refreshLatestReset();
  scheduleResetRetry();
}

function deliverLatestReset(): void {
  const message = latestReset;
  if (!message || message.id === observedResetId || localResetWork.has(message.id) || listeners.size === 0) return;

  const delivery: OfflinePrivacyResetDelivery = {
    id: message.id,
    isRetry: message.source === source || attemptedResetIds.has(message.id),
  };
  attemptedResetIds.delete(message.id);
  attemptedResetIds.add(message.id);
  while (attemptedResetIds.size > MAX_ATTEMPTED_RESET_IDS) {
    const oldest = attemptedResetIds.values().next().value as string | undefined;
    if (oldest === undefined) break;
    attemptedResetIds.delete(oldest);
  }

  let work: Array<ResetListenerResult | Promise<ResetListenerResult>>;
  try {
    // Every listener performs its scope/suspension half synchronously before
    // returning the Promise for its physical IndexedDB clear.
    work = [...listeners].map((listener) => listener(delivery));
  } catch {
    // Leave the journal unobserved. Store operations remain blocked and a
    // bounded retry repeats the idempotent wipe.
    scheduleResetRetry();
    return;
  }

  const id = message.id;
  const completion = Promise.all(work)
    .then((results) => {
      if (results.some((result) => result === false)) {
        scheduleResetRetry();
        return;
      }
      finishOfflinePrivacyReset(id);
    })
    .catch(() => {
      scheduleResetRetry();
    })
    .finally(() => {
      localResetWork.delete(id);
      // A second reset may have arrived while this clear was running. Process
      // it immediately; a failed copy of this same id waits for the retry timer
      // instead of spinning through an unbounded microtask loop.
      refreshLatestReset();
      if (latestReset?.id !== id) deliverLatestReset();
    });
  localResetWork.set(id, completion);
}

function receiveReset(message: OfflinePrivacyReset): void {
  if (message.source === source) return;
  // Merge by the ordered epoch. A delayed reset-1 event cannot replace reset-2,
  // while a reset-2 BroadcastChannel message still wins when its sender could
  // not update localStorage and this tab only has reset-1 persisted.
  adoptReset(message);
  refreshLatestReset();
  deliverLatestReset();
}

if (typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined') {
  try {
    channel = new window.BroadcastChannel(RESET_CHANNEL);
    channel.addEventListener('message', (event: MessageEvent<unknown>) => {
      const message = parseReset(event.data);
      if (message) receiveReset(message);
    });
  } catch {
    channel = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== RESET_STORAGE_KEY || !event.newValue) return;
    try {
      const message = parseReset(JSON.parse(event.newValue) as unknown);
      if (message) receiveReset(message);
    } catch {
      // Ignore malformed same-origin storage messages.
    }
  });
}

/**
 * Installs the reset callback for the mounted session owner. A missed storage
 * event is caught by the synchronous persisted-journal read here or by the next
 * store operation.
 */
export function subscribeOfflinePrivacyReset(listener: ResetListener): () => void {
  listeners.add(listener);
  refreshLatestReset();
  deliverLatestReset();
  return () => listeners.delete(listener);
}

/**
 * Writes the global pending fence before the caller starts clearing anything.
 * The caller must synchronously null/suspend all local stores, confirm their
 * idempotent clears, then acknowledge this id with finishOfflinePrivacyReset.
 */
export function beginOfflinePrivacyReset(): string {
  refreshLatestReset();
  sequence += 1;
  const epoch = Math.max(clockEpoch(), (latestReset?.epoch ?? 0) + 1);
  latestReset = {
    id: epoch + ':' + source + ':' + sequence,
    source,
    epoch,
    phase: 'pending',
  };
  persistReset(latestReset);
  return latestReset.id;
}

/**
 * Fail-closed check used before and after asynchronous IndexedDB work.
 * Reading localStorage here closes the interval before a delayed/missed event.
 */
export function offlinePrivacyResetIsCurrent(): boolean {
  refreshLatestReset();
  const resetWasPending =
    localResetWork.size > 0 ||
    (latestReset !== null && (latestReset.phase === 'pending' || latestReset.id !== observedResetId));
  deliverLatestReset();
  // The listener may finish immediately during this call, but it also changed
  // every store's scope/generation. Make the operation that discovered the
  // reset stop here; only a later operation may use the newly fenced state.
  return (
    !resetWasPending &&
    localResetWork.size === 0 &&
    (latestReset === null || (latestReset.phase === 'completed' && latestReset.id === observedResetId))
  );
}

/** Captures the reset generation into an asynchronous cache operation. */
export function captureOfflinePrivacyResetEpoch(): string | null {
  if (!offlinePrivacyResetIsCurrent()) return null;
  return latestReset?.id ?? INITIAL_RESET_EPOCH;
}

/** Display preferences can compare generations without starting cache teardown during render. */
export function currentOfflinePrivacyResetEpoch(): string {
  refreshLatestReset();
  return latestReset?.id ?? INITIAL_RESET_EPOCH;
}

/** True only while a captured operation still belongs to this privacy epoch. */
export function offlinePrivacyResetEpochIsCurrent(epoch: string): boolean {
  return offlinePrivacyResetIsCurrent() && epoch === (latestReset?.id ?? INITIAL_RESET_EPOCH);
}
